import { Map } from 'immutable';
import { createSelector } from 'reselect';
import { QueueKey } from 'flow/types/EntityKeys';
import { State } from 'flow/types/State';

export const selectAppFocus = (state: State) => state.app.get('focus');

export const selectAppIsVisibleAndFocused = (state: State) =>
    state.app.get('visible') && state.app.get('focus');

export const selectApplicationLanguage = (state: State) =>
    state.app.get('applicationLanguage');

export const selectCallCanvasSize = (state: State) =>
    state.app.get('callCanvasSize');

export const selectMakeCallsEnabled = (state: State): boolean =>
    state.app.get('makeCallsEnabled');

export const selectAutoLogin = (state: State) =>
    state.app.getIn(['electronArgs', 'autoLogin'], false);

export const selectLayoutColumns = (state: State) =>
    state.app.get('layoutColumns');

export const selectNotification = (state: State, key: string) =>
    state.app.getIn(['notifications', key]);

export const selectMissedCallCount = (state: State) =>
    state.app.get('missedCallCount');

export const selectPbxPageIsMounted = (state: State, key: QueueKey) =>
    state.app.getIn(['pbxPageMounted', key], false);

export const selectPbxSectionIsMounted = (state: State) =>
    state.app.get('pbxSectionMounted', false);

export const selectMissedCallsWidgetIsMounted = (state: State) =>
    state.app.getIn(['componentsMounted', 'missedCallsWidget']);

/** The chat session whose message list is on screen, if any. Read when the
 *  connection comes back, to refill a list that stopped being told about
 *  messages while we were away. */
export const selectMountedChatSessionKey = (state: State) =>
    state.app.get('mountedChatSessionKey');

export const selectAppState = (state: State) => state.app.get('state');

export const selectTemporaryPasswordState = (state: State) =>
    state.app.get('temporaryPasswordState');

export const selectAnalyticsConsent = (state: State) =>
    state.app.get('analyticsConsent');

export const selectConsentCategories = (state: State) =>
    state.app.get('consentCategories');

/** Whether the user has designated this desktop install as their companion. */
export const selectUseAsCompanion = (state: State) =>
    state.app.get('useAsCompanion') === true;

export const selectCompanionDeviceName = (state: State) =>
    state.app.get('companionDeviceName') ?? null;

export const selectCompanionDevices = (state: State) =>
    state.app.get('companionDevices') ?? null;

/**
 * Whether another machine has taken the companion role from this one since it was last dismissed.
 */
export const selectCompanionRoleLost = (state: State) =>
    state.app.get('companionRoleLost') === true;

/**
 * The desktop install Home has designated as this account's companion, or null when it has none.
 *
 * Which machine the handover goes to. Read off the fetched list rather than kept as a local pick:
 * Home routes every command to the device it has designated and ignores the one the command names,
 * so anything stored on this surface could only ever be a second answer to a question that is
 * already settled - and disagree with it. The user picks it on the machine itself, not here.
 */
export const selectDesignatedCompanionDevice = (state: State) =>
    state.app
        .get('companionDevices')
        ?.find((device: Map<string, unknown>) => device.get('designated')) ??
    null;

export const selectDesignatedCompanionDeviceId = (state: State) =>
    (selectDesignatedCompanionDevice(state)?.get('deviceId') as
        | string
        | undefined) ?? null;

/**
 * Whether this surface hands its calls to the desktop app when one is available.
 *
 * A setting of its own rather than a call method, because it answers a different question. The call
 * method says how this client places a call when it places one itself; this says whether it should
 * be placing it at all, or passing it to the machine the user is sitting at. Folding the two
 * together would mean turning handover on took the user's softphone away - and the softphone is
 * exactly what has to work when no desktop app is there.
 */
export const selectHandoverToDesktop = (state: State) =>
    state.app.get('handoverToDesktop') === true;

export const selectIsLoggedIn = createSelector(
    [selectAppState],
    (appState) => appState === 'ready',
);

export const selectIsLoggingOut = (state: State) =>
    state.app.get('loggingOut') === true;

export const selectIsSinglePaneMode = createSelector(
    [selectLayoutColumns],
    (layoutColumns) => layoutColumns === 1,
);
